import { getInfoValue, ifAll, IFieldDef, IGetValueArgs, isRequired as isFieldRequired, isVisible as isFieldVisible, not, TInfoValue } from "@components/smart/FieldInfo";
import { getLabelsDef } from "@components/smart/GeneralFieldDefinition";
import { createPath } from "@odata/BindingContext";
import {
    DocumentCbaCategoryDraftEntity,
    DocumentDraftEntity,
    DocumentEntity,
    ICbaCategoryTaxImpactEntity
} from "@odata/GeneratedEntityTypes";
import { CbaCategoryTaxImpactCode, DocumentTypeCode } from "@odata/GeneratedEnums";
import { dateAccountingCustomValidator } from "@pages/documents/DocumentDef";
import { isCashBasisAccountingCompany } from "@utils/CompanyUtils";
import i18next from "i18next";

import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FieldType, LabelStatus, ValidatorType, ValueType } from "../../../enums";
import { TValue } from "../../../global.types";
import { getUtcDate } from "../../../types/Date";
import { IFormDef } from "../../../views/formView/Form";
import { getAccountSelectionFieldsDef } from "../../accountAssignment/AccountAssignment.utils";
import { getCashBasisAccountingFieldsDef } from "../../cashBasisAccounting/CashBasisAccounting.utils";
import { IDefinition, IGetDefinition, TFieldDefinition, TFieldsDefinition } from "../../PageUtils";
import { isIssued, validateDecisiveDate } from "../Document.utils";
import {
    DateAccountingTypePath,
    DateReceivedTypePath,
    DraftMassAccountingDateSource,
    getCheckboxPath
} from "./DraftMassAccountingDialog.utils";

const isCashBasis: TInfoValue<boolean> = (args: IGetValueArgs): boolean => {
    return isCashBasisAccountingCompany(args.context);
};

const isReceivedCallback: TInfoValue<boolean> = (args: IGetValueArgs): boolean => {
    return !isIssued(args.storage.data.entity[DocumentDraftEntity.DocumentTypeCode]);
};

type GetConditionalFieldOptions = {
    checkboxPath?: string;
    renderCheckbox?: boolean;
    propertyPath: string;
    fieldDefinition: TFieldDefinition;
    affectedFields?: IFieldDef[];
};

const getConditionalField = (options: GetConditionalFieldOptions): TFieldsDefinition => {
    if (!options.checkboxPath) {
        return {
            [options.propertyPath]: options.fieldDefinition
        };
    }

    const isEnabled = (args: IGetValueArgs): boolean => {
        return !!args.storage.getValueByPath(options.checkboxPath);
    };

    return {
        [options.propertyPath]: {
            ...options.fieldDefinition,
            isDisabled: (args: IGetValueArgs): boolean => {
                return !isEnabled(args) || !!getInfoValue(options.fieldDefinition, "isDisabled", args);
            },
            isRequired: (args: IGetValueArgs): boolean => {
                return isEnabled(args) && isFieldRequired({ ...args, info: options.fieldDefinition });
            }
        },
        ...(options.renderCheckbox ? {
            [options.checkboxPath]: {
                type: FieldType.Checkbox,
                labelStatus: LabelStatus.Removed,
                defaultValue: false,
                width: "40px",
                customFieldStyle: { marginRight: "8px" },
                useForValidation: false,
                affectedFields: options.affectedFields ?? [{ id: options.propertyPath }]
            }
        } : {})
    };
};

export const getDefinitionFnWithDocType = (docType: DocumentTypeCode, isTaxProformaDocument: boolean): IGetDefinition => {
    const _isIssued = isIssued(docType);
    const isOrdinaryProforma = [DocumentTypeCode.ProformaInvoiceReceived, DocumentTypeCode.ProformaInvoiceIssued].includes(docType)
        && !isTaxProformaDocument;

    const cbaFieldsDef = getCashBasisAccountingFieldsDef(DocumentEntity.CbaCategory, false, _isIssued);
    const accountAssignmentFieldsDef = !isOrdinaryProforma ? getAccountSelectionFieldsDef(docType, {
        skipNoneItem: true
    }) : {};
    const accountAssignmentFieldDef = accountAssignmentFieldsDef["AccountAssignmentSelection/AccountAssignment"];

    const getDefinition: IGetDefinition = (context: IAppContext): IDefinition => {
        let groupRows: (IFieldDef[])[];
        const isCashBasisCompany = isCashBasisAccountingCompany(context);
        const accountAssignmentPath = "AccountAssignmentSelection/AccountAssignment";
        const conditionalAccountAssignmentFieldsDef = !isCashBasisCompany
            ? Object.fromEntries(Object.entries(accountAssignmentFieldsDef).map(([path, fieldDefinition]) => {
                if (path === accountAssignmentPath) {
                    return [path, fieldDefinition];
                }

                return [path, {
                    ...fieldDefinition,
                    isVisible: (args: IGetValueArgs): boolean => {
                        return !!args.storage.getValueByPath(getCheckboxPath(accountAssignmentPath))
                            && !!args.storage.getValueByPath(accountAssignmentPath)
                            && isFieldVisible({ ...args, info: fieldDefinition });
                    }
                }];
            }))
            : accountAssignmentFieldsDef;

        if (isOrdinaryProforma && !isCashBasisCompany) {
            groupRows = [
                [{ id: getCheckboxPath(DocumentDraftEntity.DateAccountingTransaction) }, { id: DateAccountingTypePath }, { id: DocumentDraftEntity.DateAccountingTransaction }]
            ];
        } else if (!isCashBasisCompany) {
            groupRows = [
                [{ id: getCheckboxPath(DocumentDraftEntity.DateAccountingTransaction) }, { id: DateAccountingTypePath }, { id: DocumentDraftEntity.DateAccountingTransaction }],
                [{ id: getCheckboxPath(accountAssignmentPath) }, { id: accountAssignmentPath }]
            ];
        } else {
            groupRows = [
                [{ id: createPath(DocumentDraftEntity.CbaCategory, DocumentCbaCategoryDraftEntity.Category) }],
                [{ id: createPath(DocumentDraftEntity.CbaCategory, DocumentCbaCategoryDraftEntity.TaxImpact) }],
                [
                    { id: createPath(DocumentDraftEntity.CbaCategory, DocumentCbaCategoryDraftEntity.IsAssetAcquisition) },
                    { id: createPath(DocumentDraftEntity.CbaCategory, DocumentCbaCategoryDraftEntity.TaxPercentage) }
                ]
            ];
        }

        const sharedDateDef: TFieldDefinition = {
            type: FieldType.ComboBox,
            isRequired: true,
            width: BasicInputSizes.M,
            fieldSettings: {
                items: [
                    {
                        label: i18next.t("Components:DraftMassAccountingDialog.Today"),
                        id: DraftMassAccountingDateSource.Today
                    },
                    {
                        label: i18next.t("Components:DraftMassAccountingDialog.DateTypeDateIssued"),
                        id: DraftMassAccountingDateSource.DateIssued
                    },
                    {
                        label: i18next.t("Components:DraftMassAccountingDialog.ByDateTaxableSupply"),
                        id: DraftMassAccountingDateSource.DateTaxableSupply
                    },
                    ...(_isIssued ? [] : [{
                        label: i18next.t("Components:DraftMassAccountingDialog.ByDateReceived"),
                        id: DraftMassAccountingDateSource.DateReceived
                    }]),
                    {
                        label: i18next.t("Components:DraftMassAccountingDialog.Own"),
                        id: DraftMassAccountingDateSource.Own
                    }
                ]
            },
            defaultValue: DraftMassAccountingDateSource.Today,
            affectedFields: [{ id: DocumentDraftEntity.DateAccountingTransaction }]
        };

        const form: IFormDef = {
            id: "DraftMassAccountingDialogSpecialForm",
            translationFiles: getDefinition.translationFiles,
            fieldDefinition: {
                ...conditionalAccountAssignmentFieldsDef,
                ...getConditionalField({
                    propertyPath: DateAccountingTypePath,
                    checkboxPath: !isCashBasisCompany ? getCheckboxPath(DocumentDraftEntity.DateAccountingTransaction) : undefined,
                    renderCheckbox: !isCashBasisCompany,
                    affectedFields: [
                        { id: DateAccountingTypePath },
                        { id: DocumentDraftEntity.DateAccountingTransaction }
                    ],
                    fieldDefinition: {
                        ...sharedDateDef,
                        label: i18next.t(`Components:DraftMassAccountingDialog.${isOrdinaryProforma ? "DateProcessed" : "DateAccounting"}`),
                        isVisible: not(isCashBasis)
                    }
                }),
                [DateReceivedTypePath]: {
                    ...sharedDateDef,
                    label: i18next.t("Components:DraftMassAccountingDialog.DateReceived"),
                    isVisible: isOrdinaryProforma ? () => false : ifAll(isCashBasis, isReceivedCallback)
                },
                ...getConditionalField({
                    propertyPath: DocumentDraftEntity.DateAccountingTransaction,
                    checkboxPath: !isCashBasisCompany ? getCheckboxPath(DocumentDraftEntity.DateAccountingTransaction) : undefined,
                    fieldDefinition: {
                        label: i18next.t("Components:DraftMassAccountingDialog.DateTypeDate"),
                        type: FieldType.Date,
                        valueType: ValueType.Date,
                        validator: {
                            type: ValidatorType.Custom,
                            settings: {
                                customValidator: (value: TValue, args: IGetValueArgs) => {
                                    return !isCashBasis(args) ? dateAccountingCustomValidator(value, args) : validateDecisiveDate(value, args);
                                }
                            }
                        },
                        isRequired: true,
                        width: BasicInputSizes.M,
                        defaultValue: () => getUtcDate(),
                        isVisible: (args: IGetValueArgs): boolean => {
                            return (args.storage.getValueByPath(DateAccountingTypePath) === DraftMassAccountingDateSource.Own)
                                || args.storage.getValueByPath(DateReceivedTypePath) === DraftMassAccountingDateSource.Own;
                        }
                    }
                }),
                [DocumentDraftEntity.Labels]: {
                    ...getLabelsDef("Document")
                },
                ...(!isOrdinaryProforma ? getConditionalField({
                    propertyPath: accountAssignmentPath,
                    checkboxPath: !isCashBasisCompany ? getCheckboxPath(accountAssignmentPath) : undefined,
                    renderCheckbox: !isCashBasisCompany,
                    fieldDefinition: {
                        ...accountAssignmentFieldDef,
                        isRequired: true,
                        width: BasicInputSizes.XL,
                        isVisible: not(isCashBasis),
                        defaultValue: null
                    }
                }) : {}),
                [createPath(DocumentDraftEntity.CbaCategory, DocumentCbaCategoryDraftEntity.Category)]: {
                    ...cbaFieldsDef[createPath(DocumentDraftEntity.CbaCategory, DocumentCbaCategoryDraftEntity.Category)],
                    width: BasicInputSizes.XL,
                    isVisible: isCashBasis
                },
                [createPath(DocumentDraftEntity.CbaCategory, DocumentCbaCategoryDraftEntity.TaxImpact)]: {
                    ...cbaFieldsDef[createPath(DocumentDraftEntity.CbaCategory, DocumentCbaCategoryDraftEntity.TaxImpact)],
                    label: i18next.t("Components:DraftMassAccountingDialog.TaxImpact"),
                    width: BasicInputSizes.XL,
                    isVisible: isCashBasis,
                    affectedFields: [
                        { id: createPath(DocumentDraftEntity.CbaCategory, DocumentCbaCategoryDraftEntity.IsAssetAcquisition) }
                    ]
                },
                [createPath(DocumentDraftEntity.CbaCategory, DocumentCbaCategoryDraftEntity.IsAssetAcquisition)]: {
                    ...cbaFieldsDef[createPath(DocumentDraftEntity.CbaCategory, DocumentCbaCategoryDraftEntity.IsAssetAcquisition)],
                    isVisible: (args: IGetValueArgs) => {
                        const taxImpact = args.storage.getValueByPath(createPath(DocumentDraftEntity.CbaCategory, DocumentCbaCategoryDraftEntity.TaxImpact)) as ICbaCategoryTaxImpactEntity;

                        return isCashBasis(args) && taxImpact?.Code === CbaCategoryTaxImpactCode.Nontax;
                    }
                },
                [createPath(DocumentDraftEntity.CbaCategory, DocumentCbaCategoryDraftEntity.TaxPercentage)]: {
                    ...cbaFieldsDef[createPath(DocumentDraftEntity.CbaCategory, DocumentCbaCategoryDraftEntity.TaxPercentage)],
                    isVisible: (args: IGetValueArgs) => {
                        const taxImpact = args.storage.getValueByPath(createPath(DocumentDraftEntity.CbaCategory, DocumentCbaCategoryDraftEntity.TaxImpact)) as ICbaCategoryTaxImpactEntity;

                        return isCashBasis(args) && taxImpact?.Code === CbaCategoryTaxImpactCode.Partial;
                    }
                }
            },
            groups: [
                {
                    id: "Common",
                    rows: groupRows
                }
            ]
        };


        return {
            form
        };
    };

    getDefinition.translationFiles = ["Components", "Common", "Document"];

    return getDefinition;
};
